import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ProblemStatementAssistantProps {
  onComplete: (title: string, description: string) => void;
  onCancel: () => void;
}

type PersonalityStyle = 'direct' | 'exploratory' | 'visionary' | 'analytical';
type AIProvider = 'openai' | 'anthropic' | 'groq' | 'grok' | 'cloudflare';

export function ProblemStatementAssistant({ onComplete, onCancel }: ProblemStatementAssistantProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [selectedStyle, setSelectedStyle] = useState<PersonalityStyle | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<AIProvider>('openai');
  const [sessionId] = useState(() => `psa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [clarityProgress, setClarityProgress] = useState({
    specificity: false,
    audience: false,
    constraints: false,
    timeline: false,
    success: false
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Analyze messages to update clarity progress
  useEffect(() => {
    const allMessages = messages.map(m => m.content.toLowerCase()).join(' ');
    setClarityProgress({
      specificity: /\b(specific|exactly|precisely|clear)\b/.test(allMessages) || allMessages.length > 100,
      audience: /\b(user|customer|audience|for whom|who is)\b/.test(allMessages),
      constraints: /\b(budget|time|resource|constraint|limit)\b/.test(allMessages),
      timeline: /\b(month|week|day|year|deadline|by when)\b/.test(allMessages),
      success: /\b(success|measure|metric|kpi|goal|achieve)\b/.test(allMessages)
    });
  }, [messages]);

  // Calculate statement score (0-100)
  const calculateScore = () => {
    const allMessages = messages.map(m => m.content.toLowerCase()).join(' ');
    let score = 0;

    // Specificity (0-25): Length and concrete details
    if (allMessages.length > 50) score += 10;
    if (allMessages.length > 150) score += 10;
    if (/\b(specific|exactly|precisely|concrete)\b/.test(allMessages)) score += 5;

    // Testability (0-20): Metrics and measurements
    if (/\b\d+\b/.test(allMessages)) score += 10; // Contains numbers
    if (/\b(measure|metric|kpi|track)\b/.test(allMessages)) score += 10;

    // Leverage (0-20): Audience and impact
    if (/\b(user|customer|audience)\b/.test(allMessages)) score += 10;
    if (/\b(market|industry|competitor)\b/.test(allMessages)) score += 10;

    // Clarity (0-20): Structure and coherence
    if (messages.length >= 4) score += 10; // Had meaningful conversation
    if (/\b(because|since|so that|in order to)\b/.test(allMessages)) score += 10; // Has reasoning

    // Constraints (0-15): Resources and limitations
    if (/\b(budget|time|resource|constraint|limit)\b/.test(allMessages)) score += 15;

    return Math.min(100, score);
  };

  const statementScore = calculateScore();
  const scoreColor = statementScore >= 70 ? '#10b981' : statementScore >= 40 ? '#f59e0b' : '#ef4444';

  const personalityStyles = [
    {
      id: 'direct' as PersonalityStyle,
      name: 'Direct',
      icon: '🎯',
      description: 'Quick, focused questions. Get to the point fast.'
    },
    {
      id: 'exploratory' as PersonalityStyle,
      name: 'Exploratory',
      icon: '🔍',
      description: 'Deep dive into context. Understand all angles.'
    },
    {
      id: 'visionary' as PersonalityStyle,
      name: 'Visionary',
      icon: '✨',
      description: 'Think big picture. Focus on impact and vision.'
    },
    {
      id: 'analytical' as PersonalityStyle,
      name: 'Analytical',
      icon: '📊',
      description: 'Data-driven questions. Metrics and evidence.'
    }
  ];

  const startConversation = async (style: PersonalityStyle) => {
    setSelectedStyle(style);
    setShowIntro(false);
    setIsLoading(true);

    try {
      const response = await fetch('/api/psa/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            { role: 'user', content: `Hi! I need help crafting a problem statement. I prefer a ${style} approach. Can you guide me through the process?` }
          ],
          style,
          provider: selectedProvider,
          sessionId
        })
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';

      if (reader) {
        // Add empty assistant message that we'll update with streaming
        setMessages([{ role: 'assistant', content: '' }]);

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          console.log('[PSA] Received chunk:', chunk);
          assistantMessage += chunk;

          // Update the message with streaming content
          setMessages([{ role: 'assistant', content: assistantMessage }]);
        }
        console.log('[PSA] Final message:', assistantMessage);
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      setMessages([{
        role: 'assistant',
        content: 'Hi! I\'m here to help you craft an exquisite problem statement. Tell me, what challenge are you trying to solve?'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: input.trim() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/psa/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: updatedMessages,
          style: selectedStyle,
          provider: selectedProvider,
          sessionId
        })
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';

      if (reader) {
        // Add empty assistant message that we'll update with streaming
        setMessages([...updatedMessages, { role: 'assistant', content: '' }]);

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          assistantMessage += chunk;

          // Update the last message with streaming content
          setMessages([...updatedMessages, { role: 'assistant', content: assistantMessage }]);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages([...updatedMessages, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const [showOptions, setShowOptions] = useState(false);
  const [options, setOptions] = useState<Array<{
    label: string;
    title: string;
    description: string;
    emphasis: string;
  }>>([]);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleComplete = async () => {
    setIsExtracting(true);
    try {
      const response = await fetch('/api/psa/extract-problem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, provider: selectedProvider })
      });

      const data = await response.json();

      if (response.ok && data.options && Array.isArray(data.options)) {
        setOptions(data.options);
        setShowOptions(true);
      } else {
        alert('Failed to extract problem statement options. Please try rephrasing or continue the conversation.');
      }
    } catch (error) {
      console.error('Error extracting problem:', error);
      alert('Failed to extract problem statement. Please try again.');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSelectOption = (option: typeof options[0]) => {
    onComplete(option.title, option.description);
  };

  // If showing options, render option selection screen
  if (showOptions) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid var(--gray-200)',
          background: 'white'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.25rem' }}>
            Choose Your Problem Statement
          </h2>
          <p style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
            Select the version that resonates most with you
          </p>
        </div>

        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '2rem 1rem',
          background: 'var(--gray-50)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {options.map((option) => (
              <div
                key={option.label}
                style={{
                  background: 'white',
                  border: '2px solid var(--gray-200)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => handleSelectOption(option)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--primary)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--gray-200)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                  <div style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    borderRadius: '50%',
                    background: 'var(--primary)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem',
                    fontWeight: '700'
                  }}>
                    {option.label}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: 'var(--gray-500)',
                    background: 'var(--gray-100)',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontStyle: 'italic'
                  }}>
                    {option.emphasis}
                  </div>
                </div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.75rem', color: 'var(--gray-900)' }}>
                  {option.title}
                </h3>
                <p style={{ fontSize: '0.9375rem', color: 'var(--gray-700)', lineHeight: '1.6', margin: 0 }}>
                  {option.description}
                </p>
              </div>
            ))}
          </div>

          <div style={{ maxWidth: '800px', margin: '2rem auto 0', textAlign: 'center' }}>
            <button
              onClick={() => setShowOptions(false)}
              className="btn btn-secondary"
            >
              ← Back to Conversation
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid var(--gray-200)',
          background: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showIntro ? 0 : '1rem' }}>
            <div style={{ flex: 1 }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.25rem' }}>
                {showIntro ? '✨ Problem Statement Assistant' : 'Crafting Your Problem Statement'}
              </h2>
              <p style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                {showIntro ? 'Let\'s make your problem statement exquisite' : 'Answer a few questions to refine your problem'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <select
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value as AIProvider)}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '6px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="openai">ChatGPT</option>
                <option value="anthropic">Claude</option>
                <option value="groq">Groq (Llama)</option>
                <option value="grok">Grok</option>
                <option value="cloudflare">Cloudflare AI</option>
              </select>
              {!showIntro && messages.length > 0 && statementScore >= 60 && (
                <button
                  onClick={handleComplete}
                  disabled={isExtracting}
                  className="btn btn-primary"
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}
                >
                  {isExtracting ? 'Processing...' : '✨ Finish & Create'}
                </button>
              )}
              <button
                onClick={() => {
                  if (!showIntro && messages.length > 0) {
                    setShowCancelConfirm(true);
                  } else {
                    onCancel();
                  }
                }}
                className="btn btn-secondary"
                style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
              >
                {!showIntro && messages.length > 0 ? 'Cancel' : 'Skip & Enter Manually'}
              </button>
            </div>
          </div>

          {/* Progress Indicator - Only show in chat mode */}
          {!showIntro && (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Clarity Progress Badges */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', flex: 1 }}>
                {[
                  { key: 'specificity', label: 'Specificity', icon: '🎯' },
                  { key: 'audience', label: 'Audience', icon: '👥' },
                  { key: 'constraints', label: 'Constraints', icon: '⚖️' },
                  { key: 'timeline', label: 'Timeline', icon: '⏱️' },
                  { key: 'success', label: 'Success', icon: '✓' }
                ].map((item) => (
                  <div
                    key={item.key}
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      background: clarityProgress[item.key as keyof typeof clarityProgress] ? 'var(--primary)' : 'var(--gray-200)',
                      color: clarityProgress[item.key as keyof typeof clarityProgress] ? 'white' : 'var(--gray-500)',
                      transition: 'all 0.3s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>

              {/* Statement Score */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '0.25rem'
              }}>
                <div style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '12px',
                  background: scoreColor,
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  whiteSpace: 'nowrap'
                }}>
                  <span>Score:</span>
                  <span style={{ fontSize: '1.25rem' }}>{statementScore}</span>
                  <span style={{ fontSize: '0.75rem', opacity: 0.9 }}>/100</span>
                </div>
                {statementScore >= 60 && (
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#10b981',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}>
                    ✓ Ready to create!
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        {showIntro ? (
          /* Intro Screen */
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '2rem 1rem',
            background: 'var(--gray-50)'
          }}>
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                How this works
              </h3>
              <p style={{ fontSize: '0.9375rem', color: 'var(--gray-700)', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                I'll guide you through a brief conversation to refine your problem statement. Together, we'll explore:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
                {[
                  { q: 'What specific outcome do you want?', desc: 'Make it concrete and measurable' },
                  { q: 'Why does this matter?', desc: 'Understand the real impact' },
                  { q: 'What have you tried already?', desc: 'Learn from past attempts' },
                  { q: 'What constraints exist?', desc: 'Time, money, resources, etc.' },
                  { q: 'How will you know you\'ve succeeded?', desc: 'Define clear success criteria' }
                ].map((item, i) => (
                  <div key={i} style={{
                    padding: '1rem',
                    background: 'var(--gray-50)',
                    borderRadius: '8px',
                    borderLeft: '3px solid var(--primary)'
                  }}>
                    <div style={{ fontSize: '0.9375rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '0.25rem' }}>
                      {item.q}
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                      {item.desc}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                padding: '1rem',
                background: '#f0f9ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                marginBottom: '2rem'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#1e40af', margin: 0, lineHeight: '1.5' }}>
                  <strong>💡 Pro tip:</strong> The better your problem statement, the better the 100 AI-generated angles will be. Take your time here.
                </p>
              </div>

              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-700)' }}>
                Choose your interaction style:
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                {personalityStyles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => startConversation(style.id)}
                    className="btn"
                    style={{
                      padding: '1rem',
                      textAlign: 'left',
                      background: 'white',
                      border: '2px solid var(--gray-200)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--primary)';
                      e.currentTarget.style.background = 'var(--gray-50)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--gray-200)';
                      e.currentTarget.style.background = 'white';
                    }}
                  >
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{style.icon}</div>
                    <div style={{ fontSize: '0.9375rem', fontWeight: '600', color: 'var(--gray-900)' }}>
                      {style.name}
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', lineHeight: '1.4' }}>
                      {style.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Chat Interface */
          <>
            {/* Messages */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '1.5rem 1rem',
              background: 'var(--gray-50)',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {messages.map((message, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <div style={{
                    maxWidth: '80%',
                    padding: '0.75rem 1rem',
                    borderRadius: '12px',
                    background: message.role === 'user' ? 'var(--primary)' : 'var(--gray-100)',
                    color: message.role === 'user' ? 'white' : 'var(--gray-900)'
                  }}>
                    {message.role === 'assistant' ? (
                      message.content ? (
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      ) : (
                        <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>Thinking...</span>
                      )
                    ) : (
                      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{message.content}</p>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div style={{
              padding: '1rem',
              borderTop: '1px solid var(--gray-200)',
              background: 'white',
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'flex-end'
            }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your response..."
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '8px',
                  resize: 'none',
                  fontSize: '0.9375rem',
                  fontFamily: 'inherit',
                  minHeight: '48px',
                  maxHeight: '120px'
                }}
                rows={1}
              />
              <button
                onClick={handleSend}
                disabled={isLoading || !input.trim()}
                className="btn btn-primary"
                style={{ padding: '0.75rem 1.5rem', whiteSpace: 'nowrap' }}
              >
                {isLoading && input.trim() === '' ? 'Waiting...' : isLoading ? 'Sending...' : 'Send'}
              </button>
            </div>

            {/* Action Buttons */}
            {messages.length >= 8 && (
              <div style={{
                padding: '1rem',
                borderTop: '1px solid var(--gray-200)',
                background: 'white',
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', margin: 0, flex: 1, minWidth: '200px' }}>
                  Looks good? Let's create your problem.
                </p>
                <button
                  onClick={handleComplete}
                  disabled={isExtracting}
                  className="btn btn-primary"
                  style={{ padding: '0.75rem 1.5rem' }}
                >
                  {isExtracting ? 'Extracting...' : '✨ Create Problem'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }} onClick={() => setShowCancelConfirm(false)}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '100%'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.75rem' }}>
              Discard conversation?
            </h3>
            <p style={{ fontSize: '0.9375rem', color: 'var(--gray-700)', marginBottom: '1.5rem', lineHeight: '1.5' }}>
              You have answered questions and your score is {statementScore}/100.
              If you cancel now, all of this progress will be lost and you'll start with an empty form.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="btn btn-primary"
              >
                Keep Working
              </button>
              <button
                onClick={() => {
                  setShowCancelConfirm(false);
                  onCancel();
                }}
                className="btn btn-secondary"
              >
                Discard & Start Over
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
